import { useEffect, useRef } from 'preact/hooks'

import { Avatar } from './Avatar.js'
import { t } from './i18n/t.js'
import type { StartAktionen } from './routes/Start.js'

/**
 * Was man mit einer Person tun kann — als Blatt von unten.
 *
 * ## Warum die vier Knöpfe nicht mehr offen in der Liste stehen
 *
 * Sie standen dort, und zwar mit Absicht: ein Tipp statt zwei, gemessen an
 * Anf. 77 („möglichst wenige Schritte"). Der Preis war eine Zeile von rund
 * 300 Pixeln je Person. Bei drei Leuten ging das auf. Bei zwölf — und darauf
 * läuft diese Familie zu — sind das dreieinhalb Bildschirmhöhen, bevor man
 * unten ankommt. Dan hat es so gesagt: „stell dir vor es kommen noch 10
 * Familienmitglieder dazu, dann scrollst du dir einen Wolf."
 *
 * Damit wird aus einem Tipp zwei. Das ist der bewusste Tausch: Ein Schritt
 * mehr für jeden Anruf gegen eine Liste, die man mit einem Blick überschaut.
 * Bei zwölf Personen ist Scrollen und Suchen teurer als der zweite Tipp.
 *
 * ## Warum ein Blatt von unten und kein eigener Bildschirm
 *
 * Ein Bildschirm hätte einen Rückweg gebraucht — also einen dritten Tipp, um
 * wieder herauszukommen. Das Blatt schließt mit einem Tipp daneben, mit Escape
 * und mit dem Knopf; darunter bleibt die Liste sichtbar, damit klar ist, wohin
 * man zurückkommt.
 */
export function PersonMenue({ person, name, roomId, aktionen, onSchliessen }: {
  person: string
  name: string
  /** Bekannter Direktraum — spart den Umweg über `POST /rooms/direct`. */
  roomId: string | null
  aktionen: StartAktionen
  onSchliessen: () => void
}) {
  const blatt = useRef<HTMLDivElement>(null)

  /*
   * Escape schließt, und der Tastaturfokus springt ins Blatt.
   *
   * Ohne den Sprung stünde der Fokus weiter auf der Zeile dahinter: Wer mit
   * der Tastatur oder einem Screenreader arbeitet, hätte ein Menü geöffnet und
   * stünde immer noch daneben.
   */
  useEffect(() => {
    blatt.current?.querySelector<HTMLButtonElement>('button')?.focus()
    const aufTaste = (e: KeyboardEvent) => { if (e.key === 'Escape') onSchliessen() }
    addEventListener('keydown', aufTaste)
    return () => removeEventListener('keydown', aufTaste)
  }, [onSchliessen])

  function tun(was: () => void) {
    onSchliessen()
    was()
  }

  return (
    <div
      class="fh-blatt"
      role="dialog"
      aria-modal="true"
      aria-label={t('Was möchtest du mit {wer} tun?', { wer: name })}
      onClick={(e) => { if (e.target === e.currentTarget) onSchliessen() }}
    >
      <div class="fh-blatt__inhalt" ref={blatt}>
        <div class="fh-blatt__kopf">
          <Avatar name={name} person={person} gross />
          <p class="fh-h2">{name}</p>
        </div>

        <div class="fh-blatt__aktionen">
          <button class="fh-btn fh-btn--gross" onClick={() => tun(() => aktionen.onChat(person, roomId))}>
            <span aria-hidden="true">💬</span> {t('Nachricht')}
          </button>
          <button class="fh-btn fh-btn--gross" onClick={() => tun(() => aktionen.onAnruf(person, roomId, 'audio'))}>
            <span aria-hidden="true">📞</span> {t('Anrufen')}
          </button>
          <button class="fh-btn fh-btn--gross" onClick={() => tun(() => aktionen.onAnruf(person, roomId, 'video'))}>
            <span aria-hidden="true">📹</span> {t('Video')}
          </button>
          <button class="fh-btn fh-btn--gross" onClick={() => tun(() => aktionen.onFoto(person, roomId))}>
            <span aria-hidden="true">📷</span> {t('Foto')}
          </button>
        </div>

        <button class="fh-btn fh-btn--quiet" onClick={onSchliessen}>{t('Schließen')}</button>
      </div>
    </div>
  )
}
